import { usePaginatedQuery } from "convex/react";
import { CheckCircle2Icon, CircleIcon } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Empty, EmptyContent, EmptyDescription } from "@/components/ui/empty";
import { ScrollArea } from "@/components/ui/scroll-area";
import { api } from "../../../../convex/_generated/api";
import type { Id } from "../../../../convex/_generated/dataModel";

interface MatterTasksProps {
	connectionId: Id<"oauthConnections">;
	clioMatterId: number;
}

export function MatterTasks({ connectionId, clioMatterId }: MatterTasksProps) {
	const pagination = usePaginatedQuery(
		api.clio.queries.listTasks,
		{ connectionId, clioMatterId },
		{ initialNumItems: 25 },
	);

	if (
		pagination.status === "LoadingFirstPage" &&
		pagination.results.length === 0
	) {
		return (
			<Empty className="p-6">
				<EmptyContent>
					<EmptyDescription>Loading tasks…</EmptyDescription>
				</EmptyContent>
			</Empty>
		);
	}
	if (pagination.results.length === 0) {
		return (
			<Empty className="p-6">
				<EmptyContent>
					<EmptyDescription>No tasks on this matter.</EmptyDescription>
				</EmptyContent>
			</Empty>
		);
	}

	return (
		<ScrollArea className="h-full min-h-0 flex-1">
			<div className="flex flex-col divide-y">
				{pagination.results.map((task) => {
					const done = Boolean(task.completedAt);
					const Icon = done ? CheckCircle2Icon : CircleIcon;
					return (
						<div key={task._id} className="flex gap-3 p-4">
							<Icon
								className={
									done
										? "mt-1 h-4 w-4 shrink-0 text-success"
										: "mt-1 h-4 w-4 shrink-0 text-muted-foreground"
								}
							/>
							<div className="flex min-w-0 flex-1 flex-col gap-1">
								<div className="flex items-baseline gap-2">
									<div className="truncate text-sm font-medium">
										{task.name}
									</div>
									{task.priority && (
										<Badge variant="outline">{task.priority}</Badge>
									)}
									{task.statusName && (
										<Badge variant="secondary" className="font-normal">
											{task.statusName}
										</Badge>
									)}
									{task.dueAt && (
										<span className="ml-auto text-xs text-muted-foreground">
											Due {new Date(task.dueAt).toLocaleDateString()}
										</span>
									)}
								</div>
								{task.description && (
									<div className="line-clamp-3 whitespace-pre-wrap text-sm text-foreground/80">
										{task.description}
									</div>
								)}
								<div className="text-xs text-muted-foreground">
									{task.assigneeName ?? "Unassigned"}
								</div>
							</div>
						</div>
					);
				})}
				{pagination.status === "CanLoadMore" && (
					<div className="p-3">
						<Button
							variant="outline"
							size="sm"
							className="w-full"
							onClick={() => pagination.loadMore(25)}
						>
							Load more
						</Button>
					</div>
				)}
			</div>
		</ScrollArea>
	);
}
